import React from "react";
import { NavLink } from "react-router-dom";
import TableCell from "../components/SmartTable/TableCell";
import Tag from "../components/Tag";
import Tooltip from "../components/Tooltip";
import { FILTER_TYPE_DATE_RANGE } from "../components/SmartTable/methods";
import { correctiveActionStatuses } from "../pages/Qam/CorrectiveActions/config";

/**
 * Generates the SmartTable column config for a dashboard "My Corrective Actions" widget.
 *
 * IOR and QAM share the same three-column layout (action, due date, view link) but differ
 * in the ID field name (`report_id` vs `audit_id`), the detail page route, and the i18n
 * prefix for title/due-date columns.
 *
 * @param {Function} format     - App date formatter ({ date }) => string
 * @param {Function} trans      - i18next translation function
 * @param {object}   config
 * @param {string}   config.idField    - Row field that holds the parent entity ID (e.g. "report_id")
 * @param {string}   config.actionPage - Route prefix for the corrective-action detail page
 * @param {string}   config.actionKey  - i18n key for the "action" column title
 * @param {string}   config.dueDateKey - i18n key for the "due date" column title
 * @returns {Array} Column descriptor array for SmartTable
 */
export const generateDashboardCorrectiveActionsColumns = (
  format,
  trans,
  { idField, actionPage, actionKey, dueDateKey }
) => [
  {
    title: actionKey,
    dataIndex: "caID",
    render: (text, row) => {
      const status = getCorrectiveActionStatusLangKey(row.status, correctiveActionStatuses, true);
      return (
        <TableCell>
          <div className="flex flex-items-center">
            {row.has_notification && <Tag className="tag-primary">{trans("container.new")}</Tag>} {row.title}
          </div>
          <div className="fs-12">
            {row[idField]}-CA{row.id}
          </div>
          <Tag style={{ backgroundColor: status.color, color: "#fff", borderColor: "transparent" }}>
            {trans(status.name)}
          </Tag>
        </TableCell>
      );
    }
  },
  {
    title: dueDateKey,
    dataIndex: "due_date",
    sorter: true,
    filterType: FILTER_TYPE_DATE_RANGE,
    render: (text, row) => <TableCell>{format({ date: row.due_date })}</TableCell>
  },
  {
    title: "container.general_words.view",
    align: "center",
    width: 60,
    render: (text, row) => (
      <Tooltip title={trans("container.general_words.view")} placement="top" className="text-primary">
        <NavLink to={`${actionPage}/${row.id}`} className="text-primary">
          <i className="ti-eye" />
        </NavLink>
      </Tooltip>
    )
  }
];

export const getCorrectiveActionStatusLangKey = (status, statuses, withObjProperties = false) => {
  if (!status) {
    return null;
  }

  const statusResult = statuses.find((item) => item.value === status);

  if (withObjProperties) {
    return statusResult;
  }

  return statusResult?.name;
};

/**
 * Determines whether the edit button should be visible for a timeline item.
 * Shared by QAM and IOR corrective action timelines.
 *
 * @param {object} itemArg - The timeline item (info/comment).
 * @param {boolean} canManage - Whether the user has management permissions.
 * @param {object} permission - The permission helper object.
 * @returns {boolean}
 */
export const timelineEditShouldBeVisible = (itemArg, canManage, permission) => {
  const item = itemArg || {};
  const rule = item.first && canManage;

  if (permission.isAuditor()) {
    return rule;
  }
  if (permission.isAuditee()) {
    if (item.type === "auditee") {
      return rule;
    }
    return false;
  }

  return rule;
};
